import { useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { DatePicker, TimePicker } from "@progress/kendo-react-dateinputs";
import { Checkbox, Input, Switch, Slider, SliderLabel } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useLocalization } from "@progress/kendo-react-intl";
import { AccessToken } from "../../../config/AccessToken";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { NotificationGroup, Notification } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loadingPanel } from "../../LoadingPanel";


export const Report = () => {

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const localizationService = useLocalization();
    const [instance, accessTokenRequest] = AccessToken();
    let languageId: any = localStorage.getItem('languageId');

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [confirmMessage, setConfirmMessage] = useState<{ reportId: number, message: string } | null>(null);

    const [isAdvanced, setIsAdvanced] = useState(false);
    const [typeReportAdvanced, setTypeReportAdvanced] = useState(false);
    const [selectedOption, setSelectedOption] = useState<any>(null);
    const [dateTimeRange, setDateTimeRange] = useState<{
        fromDate: Date | null;
        fromTime: Date | null;
        toDate: Date | null;
        toTime: Date | null;
    }>({
        fromDate: new Date(),
        toDate: new Date(),
        fromTime: new Date(),
        toTime: new Date()
    });
    const [checked, setChecked] = useState<Record<number, boolean>>({});

    const [selectedDays, setSelectedDays] = useState<number[]>([]);
    const defaultValueHour = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
    const [hour, setHour] = useState<number>(12);

    const [scheduledReports, setScheduledReports] = useState<Array<{
        id?: number;
        typeDescription: string;
        typeId: number;
        reportTypes: number[];
        days: string;
        hour: string;
    }>>([]);

    const [selectedTypes, setSelectedTypes] = useState<number[]>([]);

    const isExportDisabled = selectedTypes.length === 0 ||
        (!isAdvanced && !selectedOption) ||
        (isAdvanced && (!dateTimeRange.fromDate || !dateTimeRange.toDate));

    const isExecuteDisabled = !selectedOption || selectedTypes.length === 0 || selectedDays.length === 0 || hour === null || hour === undefined;


    const options = [
        {
            "id": -1,
            "name": localizationService.toLanguageString('configuration.analysis.path_history.custom', 'NOT_FOUND'),
            "value": -1
        },
        {
            "id": 0,
            "name": localizationService.toLanguageString('configuration.analysis.report.last_1_hour', 'NOT_FOUND'),
            "value": 60
        },
        {
            "id": 1,
            "name": localizationService.toLanguageString('configuration.analysis.report.last_3_hours', 'NOT_FOUND'),
            "value": 180
        },
        {
            "id": 2,
            "name": localizationService.toLanguageString('configuration.analysis.report.last_24_hours', 'NOT_FOUND'),
            "value": 1440
        },
        {
            "id": 3,
            "name": localizationService.toLanguageString('configuration.analysis.report.today', 'NOT_FOUND'),
            "value": 0
        },
        {
            "id": 4,
            "name": localizationService.toLanguageString('configuration.analysis.report.from_yesterday', 'NOT_FOUND'),
            "value": 1
        },
        {
            "id": 5,
            "name": localizationService.toLanguageString('configuration.analysis.report.last_3_days', 'NOT_FOUND'),
            "value": 4320
        },
        {
            "id": 6,
            "name": localizationService.toLanguageString('configuration.analysis.report.this_week', 'NOT_FOUND'),
            "value": 10080
        },
        {
            "id": 7,
            "name": localizationService.toLanguageString('configuration.analysis.report.this_month', 'NOT_FOUND'),
            "value": 43200
        }
    ];

    const checkboxLabels = [
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.signals', 'NOT_FOUND'),
            "id": 1
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.zones_access', 'NOT_FOUND'),
            "id": 2
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.devices_battery', 'NOT_FOUND'),
            "id": 3
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.distance_traveled', 'NOT_FOUND'),
            "id": 4
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.working_time', 'NOT_FOUND'),
            "id": 5
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.devices_status', 'NOT_FOUND'),
            "id": 6
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.distance_traveled_odometer', 'NOT_FOUND'),
            "id": 7
        }
    ];

    const days = [
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.monday', 'NOT_FOUND'),
            "value": 1
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.tuesday', 'NOT_FOUND'),
            "value": 2
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.wednesday', 'NOT_FOUND'),
            "value": 3
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.thursday', 'NOT_FOUND'),
            "value": 4
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.friday', 'NOT_FOUND'),
            "value": 5
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.saturday', 'NOT_FOUND'),
            "value": 6
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.sunday', 'NOT_FOUND'),
            "value": 0
        }
    ];

    const formatScheduledDays = (rawDays: string): string => {
        if (!rawDays || typeof rawDays !== 'string') {
            return '';
        }

        const labelsByValue = new Map(days.map((day) => [day.value, day.name]));
        const tokens = rawDays
            .split(',')
            .map((token) => token.trim())
            .filter((token) => token.length > 0);

        const numericDays = tokens
            .map((token) => Number(token))
            .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6);

        if (numericDays.length > 0) {
            const orderedNumericDays = Array.from(new Set(numericDays)).sort((a, b) => a - b);
            return orderedNumericDays
                .map((dayValue) => labelsByValue.get(dayValue) || String(dayValue))
                .join(', ');
        }

        const orderByName = new Map(days.map((day) => [day.name.toLowerCase(), day.value]));

        return tokens
            .sort((a, b) => {
                const aKey = a.toLowerCase();
                const bKey = b.toLowerCase();
                const aOrder = orderByName.get(aKey) ?? Number.MAX_SAFE_INTEGER;
                const bOrder = orderByName.get(bKey) ?? Number.MAX_SAFE_INTEGER;
                return aOrder - bOrder;
            })
            .join(', ');
    };

    const toggleType = (value: number) => {
        setSelectedTypes((prev) =>
            prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value]
        );
    };

    const formatDateTimeUtc = (date: Date): string => {
        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();
        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
        const seconds = date.getUTCSeconds().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    const toCustomWeekDayIndex = (jsDay: number): number => {
        // JS: Sunday=0..Saturday=6, App: Monday=0..Sunday=6
        return (jsDay + 6) % 7;
    };

    const convertScheduledTimeToUtc = (days: number[], localHour: number) => {
        const referenceLocal = new Date();
        referenceLocal.setHours(localHour, 0, 0, 0);

        const utcHour = referenceLocal.getUTCHours().toString().padStart(2, "0");
        const utcMinutes = referenceLocal.getUTCMinutes().toString().padStart(2, "0");
        const utcSeconds = referenceLocal.getUTCSeconds().toString().padStart(2, "0");

        const referenceCustomDay = toCustomWeekDayIndex(referenceLocal.getDay());
        const utcDays = days.map((day) => {
            const dayDate = new Date(referenceLocal);
            dayDate.setDate(referenceLocal.getDate() + (day - referenceCustomDay));
            return toCustomWeekDayIndex(dayDate.getUTCDay());
        });

        return {
            days: Array.from(new Set(utcDays)),
            hour: `${utcHour}:${utcMinutes}:${utcSeconds}`
        };
    };

    const handleExport = async (format: "csv" | "xlsx") => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            let fromDateTime: string;
            let toDateTime: string;

            if (isAdvanced) {
                // Modalità custom: usa fromDate/fromTime e toDate/toTime
                const fromDateObj = dateTimeRange.fromDate || new Date();
                const fromTimeObj = dateTimeRange.fromTime || new Date();
                const toDateObj = dateTimeRange.toDate || new Date();
                const toTimeObj = dateTimeRange.toTime || new Date();

                // Combina data e ora locale, poi converte in stringa UTC per il backend
                const combinedFrom = new Date(
                    fromDateObj.getFullYear(),
                    fromDateObj.getMonth(),
                    fromDateObj.getDate(),
                    fromTimeObj.getHours(),
                    fromTimeObj.getMinutes(),
                    fromTimeObj.getSeconds(),
                    0
                );

                const combinedTo = new Date(
                    toDateObj.getFullYear(),
                    toDateObj.getMonth(),
                    toDateObj.getDate(),
                    toTimeObj.getHours(),
                    toTimeObj.getMinutes(),
                    toTimeObj.getSeconds(),
                    0
                );

                fromDateTime = formatDateTimeUtc(combinedFrom);
                toDateTime = formatDateTimeUtc(combinedTo);
            } else {
                // Modalità predefinita: calcola basandosi su selectedOption.value (minuti)
                // Lavora in ora locale per avere la mezzanotte corretta
                const now = new Date();
                now.setSeconds(0);
                now.setMilliseconds(0);

                // Caso speciale per "oggi" (id 3): parte dalla mezzanotte locale
                if (selectedOption.id === 3) {
                    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
                    fromDateTime = formatDateTimeUtc(midnight);
                    toDateTime = formatDateTimeUtc(now);
                }
                // Caso speciale per "da ieri" (id 4): parte dalla mezzanotte del giorno precedente locale
                else if (selectedOption.id === 4) {
                    const yesterday = new Date(now);
                    yesterday.setDate(yesterday.getDate() - 1);
                    const yesterdayMidnight = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
                    fromDateTime = formatDateTimeUtc(yesterdayMidnight);
                    toDateTime = formatDateTimeUtc(now);
                }
                else {
                    // Per gli altri casi (ultime X ore): sottrai i minuti dall'ora corrente
                    const minutesAgo = selectedOption.value;
                    const fromDate = new Date(now.getTime() - minutesAgo * 60000);
                    fromDateTime = formatDateTimeUtc(fromDate);
                    toDateTime = formatDateTimeUtc(now);
                }
            }

            const payload = {
                reportIds: selectedTypes,
                fromDatetime: fromDateTime,
                toDatetime: toDateTime,
                exportType: format === "csv" ? "CSV" : "Excel"
            };

            try {
                await axios.post(`${SERVER_URL}/report`, payload, { headers });

                setSuccessMessage(localizationService.toLanguageString('configuration.analysis.report.email_sent_success', 'Report inviato via email con successo!'));
            } catch (error: any) {
                console.error(error);
                setErrorMessage(localizationService.toLanguageString('configuration.analysis.report.export_error', 'Errore durante l\'esportazione del report') + ": " + (error?.response?.data?.message || error?.message));
            } finally {
                setLoading(false);
            }
        };

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
    };


    // Gestione checkbox
    const toggleDay = (value: number) => {
        setSelectedDays((prev) =>
            prev.includes(value)
                ? prev.filter((d) => d !== value)
                : [...prev, value]
        );
    };

    const mergeDateAndTime = (date: Date, time: Date) => {
        const merged = new Date(date);
        merged.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
        return merged;
    };

    const isValidYear = (date: Date) => {
        const year = date.getFullYear();
        return year >= 1000;
    };

    const handleChangeDate = (event: any) => {
        const name = event?.target?.props?.name ?? event?.target?.name;
        const value = event?.value ?? event?.target?.value;

        if (!name) {
            return;
        }

        if (value && !isValidYear(value)) {
            return;
        }

        setDateTimeRange((prev) => {
            const updated = { ...prev, [name]: value };

            if (name === "toDate" && value && prev.fromDate && value < prev.fromDate) {
                return prev;
            }

            if (name === "fromDate" && value && prev.fromTime) {
                updated.fromTime = mergeDateAndTime(value, prev.fromTime);
            }

            if (name === "toDate" && value && prev.toTime) {
                updated.toTime = mergeDateAndTime(value, prev.toTime);
            }

            return updated;
        });
    };

    const handleBlurDate = (event: any) => {
        const name = event?.target?.props?.name ?? event?.target?.name;
        const value = event?.target?.value;

        if (!name || !value) {
            return;
        }

        setDateTimeRange((prev) => {
            const updated = { ...prev };

            if (name === "toDate" && prev.fromDate && value < prev.fromDate) {
                updated.toDate = null;
            }

            if (name === "fromDate" && prev.toDate && value > prev.toDate) {
                updated.toDate = null;
            }

            return updated;
        });
    };

    const handleChangeTime = (event: any) => {
        const name = event?.target?.props?.name ?? event?.target?.name;
        const value = event?.value ?? event?.target?.value;

        if (!name) {
            return;
        }

        setDateTimeRange((prev) => {
            const updated = { ...prev, [name]: value };

            if (name === "toTime" && value && prev.fromTime && value < prev.fromTime) {
                return prev;
            }

            if (name === "fromTime" && value && prev.fromDate) {
                updated.fromDate = mergeDateAndTime(prev.fromDate, value);
            }

            if (name === "toTime" && value && prev.toDate) {
                updated.toDate = mergeDateAndTime(prev.toDate, value);
            }

            return updated;
        });
    };

    const handleBlurTime = (event: any) => {
        const name = event?.target?.props?.name ?? event?.target?.name;
        const value = event?.target?.value;

        if (!name || !value) {
            return;
        }

        setDateTimeRange((prev) => {
            const updated = { ...prev };

            if (name === "toTime" && prev.fromTime && value < prev.fromTime) {
                updated.toTime = null;
            }

            if (name === "fromTime" && prev.toTime && value > prev.toTime) {
                updated.fromTime = null;
            }

            return updated;
        });
    };

    // Formatta l'ora come HH:MM
    const formatHour = (v: number) => `${v.toString().padStart(2, "0")}:00`;

    const formatScheduledUtcHourToLocal = (utcHour: string): string => {
        if (!utcHour) {
            return "";
        }

        const parts = utcHour.split(":").map((value) => Number(value));
        if (parts.length < 2 || parts.some((value) => Number.isNaN(value))) {
            return utcHour;
        }

        const [hours, minutes, seconds = 0] = parts;
        const referenceDate = new Date();
        referenceDate.setUTCHours(hours, minutes, seconds, 0);

        const localHours = referenceDate.getHours().toString().padStart(2, "0");
        const localMinutes = referenceDate.getMinutes().toString().padStart(2, "0");

        return `${localHours}:${localMinutes}`;
    };

    const handleChange = (e: any) => {
        const v = Math.round(e.value);  // <-- forza l’ora ad un intero
        setHour(v);
    };

    const execute = async () => {
        if (!selectedOption || selectedDays.length === 0 || hour === null || hour === undefined) {
            setErrorMessage(localizationService.toLanguageString('configuration.analysis.report.complete_parameters', 'Compila tutti i parametri prima di eseguire.'));
            return;
        }

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            const { days: utcDays, hour: utcHour } = convertScheduledTimeToUtc(selectedDays, Math.round(hour));

            const payload = {
                reportIds: selectedTypes,
                typeId: selectedOption.id,
                hour: utcHour,
                days: utcDays
            };

            try {
                await axios.post(`${SERVER_URL}/report/scheduled`, payload, { headers });

                // Ricarica la lista dei report schedulati dopo il successo
                await getReportList();

                // Reset dei campi
                setSelectedTypes([]);
                setSelectedDays([]);
                setHour(12);
                setSelectedOption(null);

                setSuccessMessage(localizationService.toLanguageString('configuration.analysis.report.scheduled_success', 'Report schedulato con successo!'));
            } catch (error: any) {
                console.error(error);
                setErrorMessage(localizationService.toLanguageString('configuration.analysis.report.scheduling_error', 'Errore durante la schedulazione del report') + ": " + (error?.response?.data?.message || error?.message));
            } finally {
                setLoading(false);
            }
        };

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
    };

    const getReportList = async () => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            await axios.get(`${SERVER_URL}/report/scheduled`, { headers })
                .then((res) => {
                    const data: any = res?.data?.data
                    // Mappa i dati del server per adattarli alla struttura frontend
                    const mappedData = data.map((item: any) => ({
                        ...item,
                        reportTypes: Array.isArray(item.reportId) ? item.reportId : [item.reportId]
                    }));
                    setScheduledReports(mappedData);
                    setLoading(false);
                }).catch((data) => {
                    //setError(data?.message);
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const deleteScheduledReport = async (reportId: number) => {
        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                await axios.delete(`${SERVER_URL}/report/scheduled/${reportId}`, { headers });

                // Ricarica la lista dei report schedulati dopo l'eliminazione
                await getReportList();

                setSuccessMessage(localizationService.toLanguageString('configuration.analysis.report.deleted_success', 'Report eliminato con successo!'));
            } catch (error: any) {
                console.error(error);
                setErrorMessage(localizationService.toLanguageString('configuration.analysis.report.deletion_error', "Errore durante l'eliminazione del report") + ": " + (error?.response?.data?.message || error?.message));
            } finally {
                setLoading(false);
            }
        };

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
    };

    //  call API on page load
    useEffect(() => {
        if (typeReportAdvanced) {
            getReportList()
        }
    }, [typeReportAdvanced])

    // Auto-close success message after 3 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    // Auto-close error message after 3 seconds
    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => {
                setErrorMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [errorMessage]);

    // Auto-close confirm message after 3 seconds
    useEffect(() => {
        if (confirmMessage) {
            const timer = setTimeout(() => {
                setConfirmMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [confirmMessage]);

    const onChangeIntervalTime = (ev: any) => {
        const value = ev.value;
        setSelectedOption(value);
        if (value && value.id === -1) {
            setIsAdvanced(true);
        } else {
            setIsAdvanced(false);
        }
    };

    return (
        <>
            <div style={{ margin: '20px', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', width: 'calc(100% - 40px)' }} >
                {loading && loadingPanel}

                <NotificationGroup
                    style={{
                        right: 0,
                        bottom: 50,
                        alignItems: "flex-start",
                        flexWrap: "wrap-reverse",
                    }}
                >
                    <Fade enter={true} exit={true}>
                        {successMessage && (
                            <Notification
                                type={{ style: "success", icon: true }}
                                closable={true}
                                onClose={() => setSuccessMessage(null)}
                            >
                                <span>{successMessage}</span>
                            </Notification>
                        )}
                        {errorMessage && (
                            <Notification
                                type={{ style: "error", icon: true }}
                                closable={true}
                                onClose={() => setErrorMessage(null)}
                            >
                                <span>{errorMessage}</span>
                            </Notification>
                        )}
                        {confirmMessage && (
                            <Notification
                                type={{ style: "warning", icon: true }}
                                closable={true}
                                onClose={() => setConfirmMessage(null)}
                            >
                                <div>
                                    <div style={{ marginBottom: '10px' }}>{confirmMessage.message}</div>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <Button
                                            themeColor="error"
                                            size="small"
                                            onClick={() => {
                                                deleteScheduledReport(confirmMessage.reportId);
                                                setConfirmMessage(null);
                                            }}
                                        >
                                            {localizationService.toLanguageString('configuration.analysis.report.confirm', 'NOT_FOUND')}
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => setConfirmMessage(null)}
                                        >
                                            {localizationService.toLanguageString('configuration.analysis.report.cancel', 'NOT_FOUND')}
                                        </Button>
                                    </div>
                                </div>
                            </Notification>
                        )}
                    </Fade>
                </NotificationGroup>

                <div>
                    <h1>{localizationService.toLanguageString('configuration.analysis.report.report_title', 'NOT_FOUND')}</h1>

                    <Tooltip position="top" anchorElement="target">
                        <div style={{ display: 'flex', marginTop: '10px', alignItems: 'center', marginBottom: typeReportAdvanced ? '10px' : '0px' }}>
                            <span
                                style={{ marginRight: '10px' }}
                                title={localizationService.toLanguageString('configuration.analysis.report.switch_report_type_tooltip', 'NOT_FOUND')}
                            >
                                {localizationService.toLanguageString('configuration.analysis.report.switch_report_type', 'NOT_FOUND')}
                            </span>
                            <Switch
                                onChange={() => {
                                    setTypeReportAdvanced(!typeReportAdvanced);
                                    if (!typeReportAdvanced) {
                                        setIsAdvanced(false);
                                    }
                                }}
                                checked={typeReportAdvanced}
                                size={'large'}
                            />
                            <b style={{ marginLeft: '10px' }}>
                                {!typeReportAdvanced ?
                                    localizationService.toLanguageString('configuration.analysis.report.standard', 'NOT_FOUND')
                                    :
                                    localizationService.toLanguageString('configuration.analysis.report.scheduled', 'NOT_FOUND')
                                }
                            </b>
                        </div>
                    </Tooltip>

                    <div style={{ margin: '10px 0px 10px 0px' }}>   </div>

                    {/* Dropdown or DatePickers */}
                    <DropDownList
                        data={options}
                        textField="name"
                        dataItemKey="value"
                        style={{ width: '70%' }}
                        value={selectedOption}
                        onChange={onChangeIntervalTime}
                        label={localizationService.toLanguageString('configuration.analysis.report.predefined_interval', 'NOT_FOUND')}
                        title={localizationService.toLanguageString('configuration.analysis.report.predefined_interval', 'NOT_FOUND')}
                    />

                    {
                        //advanced timeslot
                        isAdvanced &&
                        <div style={{ margin: '10px 0px 20px 0px' }}>
                            <label>{localizationService.toLanguageString('configuration.analysis.report.pick_interval', 'NOT_FOUND')}</label>
                            <div style={{ display: 'flex' }}>
                                <label style={{ width: '30px' }}>{localizationService.toLanguageString('configuration.analysis.report.from', 'NOT_FOUND')}</label>
                                <DatePicker
                                    name="fromDate"
                                    value={dateTimeRange.fromDate}
                                    format="dd/MM/yyyy"
                                    width={190}
                                    onChange={handleChangeDate}
                                    onBlur={handleBlurDate}
                                />
                                <div style={{ marginLeft: '10px' }}>
                                    <TimePicker
                                        name="fromTime"
                                        value={dateTimeRange.fromTime}
                                        format="HH:mm:ss"
                                        width={150}
                                        onChange={handleChangeTime}
                                        onBlur={handleBlurTime}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', margin: '10px 0px 0px 0px' }}>
                                <label style={{ width: '30px' }}>{localizationService.toLanguageString('configuration.analysis.report.to', 'NOT_FOUND')}</label>
                                <DatePicker
                                    name="toDate"
                                    value={dateTimeRange.toDate}
                                    format="dd/MM/yyyy"
                                    width={190}
                                    min={dateTimeRange.fromDate || undefined}
                                    onChange={handleChangeDate}
                                    onBlur={handleBlurDate}
                                />
                                <div style={{ marginLeft: '10px' }}>
                                    <TimePicker
                                        name="toTime"
                                        value={dateTimeRange.toTime}
                                        format="HH:mm:ss"
                                        width={150}
                                        min={dateTimeRange.fromTime || undefined}
                                        onChange={handleChangeTime}
                                        onBlur={handleBlurTime}
                                    />
                                </div>
                            </div>
                        </div>
                    }

                    <div>
                        {/* Checkbox */}
                        <div className="grid grid-cols-2 gap-2">
                            {checkboxLabels.map((label, i) => (
                                <div key={i} className="col-span-1">
                                    <Checkbox
                                        key={i}
                                        label={label.name}
                                        checked={selectedTypes.includes(label.id)}
                                        onChange={() => toggleType(label.id)}
                                    />

                                    {/* Figli solo per l'ultima checkbox */}
                                    {i === checkboxLabels.length - 1 && !!checked[i] && !typeReportAdvanced && (
                                        <div className="ml-6 mt-2 space-y-3 border-l pl-4 border-gray-200">
                                            <div>
                                                <label className="block text-xs font-medium mb-1">
                                                    {localizationService.toLanguageString('configuration.analysis.report.min_pause_interval', 'NOT_FOUND')}
                                                </label>
                                                <Input
                                                    placeholder={localizationService.toLanguageString('configuration.analysis.report.enter_value', 'NOT_FOUND')}
                                                    style={{ width: '130px', margin: '0px 0px 0px 10px' }}
                                                />
                                            </div>

                                            <div>
                                                <Checkbox
                                                    label={localizationService.toLanguageString('configuration.analysis.report.exclude_zero_access', 'NOT_FOUND')}
                                                />
                                            </div>

                                            <div>
                                                <label className="ml-6 mt-2 space-y-3 border-l pl-4 border-gray-200">
                                                    {localizationService.toLanguageString('configuration.analysis.report.acceleration_threshold', 'NOT_FOUND')}
                                                </label>
                                                <Input
                                                    placeholder={localizationService.toLanguageString('configuration.analysis.report.enter_value', 'NOT_FOUND')}
                                                    style={{ width: '130px', margin: '0px 0px 0px 10px' }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {!typeReportAdvanced ?
                            <>
                                {/*Bottoni Export */}
                                <div className="flex justify-end gap-3 border-t pt-4">
                                    <Button
                                        themeColor="primary"
                                        title={localizationService.toLanguageString('configuration.analysis.report.export_to_csv', 'NOT_FOUND')}
                                        onClick={() => handleExport("csv")}
                                        disabled={isExportDisabled}
                                    >
                                        {localizationService.toLanguageString('configuration.analysis.report.export_to_csv', 'NOT_FOUND')}
                                    </Button>
                                    <Button
                                        themeColor="primary"
                                        style={{ margin: '0px 0px 0px 10px' }}
                                        title={localizationService.toLanguageString('configuration.analysis.report.export_to_xlsx', 'NOT_FOUND')}
                                        onClick={() => handleExport("xlsx")}
                                        disabled={isExportDisabled}
                                    >
                                        {localizationService.toLanguageString('configuration.analysis.report.export_to_xlsx', 'NOT_FOUND')}
                                    </Button>
                                </div>

                            </>
                            :
                            <div className="flex justify-end gap-3 border-t pt-4">
                                <Button
                                    themeColor="primary"
                                    title={localizationService.toLanguageString('configuration.analysis.report.execute', 'NOT_FOUND')}
                                    onClick={execute}
                                    disabled={isExecuteDisabled}
                                >
                                    {localizationService.toLanguageString('configuration.analysis.report.execute', 'NOT_FOUND')}
                                </Button>
                            </div>
                        }

                    </div>
                </div>

                {/*scheduled report*/}
                {typeReportAdvanced && (
                    <div style={{ display: 'flex', flexDirection: 'column' }} >

                        <div style={{ display: 'flex', background: 'lightgrey', borderRadius: '5px', padding: '10px', marginTop: '20px' }}>

                            <h3 style={{ width: '150px' }}>{localizationService.toLanguageString('configuration.analysis.report.days', 'NOT_FOUND')}</h3>

                            {/*days*/}
                            <div style={{ display: 'flex' }}>
                                {days.map((day: any) => (
                                    <div key={day.value} style={{ marginBottom: '8px', textAlign: 'center' }}>
                                        <div style={{ marginRight: '10px' }}>
                                            {day.name}
                                        </div>
                                        <Checkbox
                                            checked={selectedDays.includes(day.value)}
                                            onChange={() => toggleDay(day.value)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/*slider*/}
                        <div style={{ background: 'lightgrey', borderRadius: '5px', padding: '10px', marginTop: '20px' }}>
                            <div>
                                <h3>{localizationService.toLanguageString('configuration.analysis.report.hour', 'NOT_FOUND')}</h3>

                                <Slider
                                    min={0}
                                    max={23}
                                    step={1}
                                    value={hour}
                                    onChange={handleChange}
                                >
                                    {defaultValueHour.map((value: any, id: any) => (
                                        <SliderLabel key={id} position={value} />
                                    ))}
                                </Slider>
                                <div
                                    style={{
                                        padding: "3px 8px",
                                        color: "#1A8692",
                                        borderRadius: 4,
                                        fontSize: 14,
                                        textAlign: "center"
                                    }}
                                >
                                    {formatHour(hour)}
                                </div>

                            </div>

                        </div>

                        {scheduledReports.length > 0 && (
                            <div style={{ marginTop: '30px' }}>
                                <h3>{localizationService.toLanguageString('configuration.analysis.report.scheduled', 'NOT_FOUND')}</h3>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#f0f0f0' }}>
                                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>{localizationService.toLanguageString('configuration.analysis.report.interval', 'NOT_FOUND')}</th>
                                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>{localizationService.toLanguageString('configuration.analysis.report.type_description', 'NOT_FOUND')}</th>
                                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>{localizationService.toLanguageString('configuration.analysis.report.days', 'NOT_FOUND')}</th>
                                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>{localizationService.toLanguageString('configuration.analysis.report.hour', 'NOT_FOUND')}</th>
                                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>{localizationService.toLanguageString('configuration.analysis.report.delete', 'NOT_FOUND')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {scheduledReports.map((report, idx) => (
                                            <tr key={idx}>
                                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{options.find((option: any) => option.id === report.typeId)?.name}</td>
                                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{report.reportTypes?.map(typeId => checkboxLabels.find((type: any) => type.id === typeId)?.name).join(', ')}</td>
                                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{formatScheduledDays(report.days)}</td>
                                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{formatScheduledUtcHourToLocal(report.hour)}</td>
                                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                                                    <Button
                                                        themeColor="error"
                                                        onClick={() => {
                                                            if (report.id) {
                                                                setConfirmMessage({
                                                                    reportId: report.id,
                                                                    message: localizationService.toLanguageString('configuration.analysis.report.delete_confirm', 'Sei sicuro di voler eliminare questo report schedulato?')
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        {localizationService.toLanguageString('configuration.analysis.report.delete', 'NOT_FOUND')}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                    </div>
                )}
            </div>
        </>
    );
};